import { useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Label } from 'components/ui/label';

import { useGetFolderList } from 'features/chat/hooks/use-kb';

import { SettingToggleField } from '../setting-toggle-field';
import { getAskAgentOwnerToAddMessage } from '../ask-agent-owner-message';
import { AgentConfigFolderCard } from './agents-config-folder-card';
import { KnowledgeRecallModal } from './knowledge-recall-modal';

type KnowledgeBaseSectionProps = {
  enabled: boolean;
  canEdit?: boolean;
  projectKey: string;
  selectedFolderIds: string[];
  onSelectedFolderIdsChange: (value: string[]) => void;
  enableQueryEnhancement: boolean;
  onEnableQueryEnhancementChange: (value: boolean) => void;
  scoreThreshold: number;
  onScoreThresholdChange: (value: number) => void;
  topK: number;
  onTopKChange: (value: number) => void;
  denseWeight: number;
  onDenseWeightChange: (value: number) => void;
};

export function KnowledgeBaseSection({
  enabled,
  canEdit = true,
  projectKey,
  selectedFolderIds,
  onSelectedFolderIdsChange,
  enableQueryEnhancement,
  onEnableQueryEnhancementChange,
  scoreThreshold,
  onScoreThresholdChange,
  topK,
  onTopKChange,
  denseWeight,
  onDenseWeightChange,
}: KnowledgeBaseSectionProps) {
  const { t } = useTranslation();
  const [isKnowledgeRecallOpen, setIsKnowledgeRecallOpen] = useState(false);
  const { data: folderListData } = useGetFolderList(projectKey, !!projectKey);
  const folders = folderListData?.data?.folders || [];

  const keywordWeight = useMemo(() => Number((1 - denseWeight).toFixed(2)), [denseWeight]);
  const semanticPercentage = Math.round(denseWeight * 100);
  const keywordPercentage = Math.round(keywordWeight * 100);
  const searchType = denseWeight >= 0.999 ? t('SEMANTIC') || 'Semantic' : t('MIXED') || 'Mixed';
  const selectedFolders = folders.filter((folder) => selectedFolderIds.includes(folder._id));

  const handleRemoveKB = (id: string) => {
    onSelectedFolderIdsChange(selectedFolderIds.filter((folderId) => folderId !== id));
  };

  return (
    <div className="space-y-6">
      {/* <SettingToggleField
        label={t('QUESTION_ENHANCEMENT') || 'Question Enhancement'}
        description={
          t('ENHANCE_QUESTIONS_FOR_BETTER_RETRIEVAL') ||
          'Enhance user questions for better retrieval quality.'
        }
        checked={enableQueryEnhancement}
        onCheckedChange={onEnableQueryEnhancementChange}
        disabled={!enabled}
        showInfoIcon
      /> */}

      {/* <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium text-foreground">
              {t('KNOWLEDGE_RECALL') || 'Knowledge Recall'}
            </Label>
            <Info className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setIsKnowledgeRecallOpen(true)}
            disabled={!enabled}
            className="rounded-lg bg-muted px-4 py-3 text-sm text-foreground transition-colors hover:bg-muted/80 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="text-muted-foreground">
              <span className="dark:text-[#FFFFFF]">{t('KNOWLEDGE_RELEVANCE_LABEL') || 'Knowledge Relevance:'}</span>
            </span>{' '}
            <span className="font-semibold text-primary dark:text-[#FFFFFF]">{scoreThreshold.toFixed(2)}</span>
          </button>

          <button
            type="button"
            onClick={() => setIsKnowledgeRecallOpen(true)}
            disabled={!enabled}
            className="rounded-lg bg-muted px-4 py-3 text-sm text-foreground transition-colors hover:bg-muted/80 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="text-muted-foreground dark:text-[#FFFFFF]">{t('RECALL_NUMBER_LABEL') || 'Recall Number:'}</span>{' '}
            <span className="font-semibold text-primary dark:text-[#FFFFFF]">{topK}</span>
          </button>

          <button
            type="button"
            onClick={() => setIsKnowledgeRecallOpen(true)}
            disabled={!enabled}
            className="rounded-lg bg-muted px-4 py-3 text-left text-sm text-foreground transition-colors hover:bg-muted/80 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="text-muted-foreground dark:text-[#FFFFFF]">{t('SEARCH_WEIGHT_LABEL') || 'Search Weight:'}</span>{' '}
            <span className="font-semibold text-primary dark:text-[#FFFFFF]">
              {searchType} ({t('SEMANTIC_LABEL') || 'Semantic'}: {semanticPercentage}%, {t('KEYWORDS_LABEL') || 'Keywords'}: {keywordPercentage}%)
            </span>
          </button>
        </div>
      </div> */}

      {selectedFolders.length > 0 ? (
        <div className="grid max-h-[40vh] gap-4 overflow-y-auto">
          {selectedFolders.map((folder) => (
            <AgentConfigFolderCard
              key={folder._id}
              folder={folder}
              onRemove={handleRemoveKB}
              disabled={!enabled}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-border bg-muted/20 px-5 py-4 text-sm text-muted-foreground">
          {canEdit
            ? t('NO_KNOWLEDGE_FOLDERS_CONNECTED') ||
              'No knowledge folders connected yet. Add a folder to ground responses with retrieval.'
            : getAskAgentOwnerToAddMessage(t, t('A_FOLDER') || 'a folder')}
        </div>
      )}

      <KnowledgeRecallModal
        open={isKnowledgeRecallOpen}
        onOpenChange={setIsKnowledgeRecallOpen}
        scoreThreshold={scoreThreshold}
        onScoreThresholdChange={onScoreThresholdChange}
        topK={topK}
        onTopKChange={onTopKChange}
        denseWeight={denseWeight}
        onDenseWeightChange={onDenseWeightChange}
      />
    </div>
  );
}
